/**
 * Admin → Portfolios → Assign to… (Banks arc step 4, migration 0115; TRADE-CONS 1a / 1b,
 * migrations 0128 / 0129). Pedro's S1-UX plan (2026-10-29), two tabs:
 *
 * - **Attach to Agreements and Models** — one row per portfolio: its Agreement (a portfolio
 *   belongs to one agreement at a time, P2; an RM's link filed on an agreement shows in blue) and
 *   its Model: the portfolio's DEFAULT model when it is viewed or traded on its own (the user can
 *   always choose another there). Changed or removed only here; never lost by joining a
 *   consolidation. "Apply a model to an agreement…" sets it on all of an agreement's portfolios.
 * - **Attach to Consolidations** — one row per portfolio: Portfolio, Agreement (read-only),
 *   Consolidation (a picker of the row's agreement's consolidations, U1; "New consolidation…"
 *   creates one; rename / delete from the menu beside it) and Consolidation model (the
 *   consolidation's own model, None allowed; a new one starts at None, U2; changing it on any
 *   row changes it for the consolidation). A consolidation left with one portfolio is refused
 *   at Save, naming it (S1-Q7d); left with none, it is deleted.
 *
 * Each tab: Edit → Save changes / Discard (§3g Pattern A, leave-page guard); each column changed
 * only with its own permission (F2: portfolios.manage / portfolios.models /
 * portfolios.consolidate), greyed with the reason otherwise. Switching tab goes through the
 * leave guard and the tab left is unmounted (C-248). Headings filter and sort (P2, U3).
 */
import { useMemo, useState } from 'react';
import {
  ActionIcon, Alert, Anchor, Badge, Button, Group, Menu, Modal, Select, Stack, Table, Tabs, Text, TextInput, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowBackUp, IconDots, IconFileDescription, IconPencil, IconStack2 } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { agreementPortfoliosApi, type PortfolioLinkRow } from '../api/agreementPortfolios';
import { ApiError } from '../api/client';
import { modelsApi } from '../api/models';
import { portfolioConsolidationsApi, portfolioModelsApi } from '../api/portfolioConsolidations';
import { DisabledTooltipButton } from '../components/DisabledTooltipButton';
import { useDirtyForm } from '../components/DirtyFormProvider';
import { AgreementPicker } from '../components/EntityPicker';
import { useGridHeaders, type GridCol } from '../components/GridHeaders';
import { useDirtyFormGuard } from '../hooks/useDirtyFormGuard';
import { useReportTabDirty } from '../components/TabDirty';
import { usePermissions } from '../hooks/usePermissions';

const errMsg = (e: unknown) => (e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Something went wrong.');
const NEW = '__new__';
const NONE = '__none__';
const OVERVIEW = ['agreement-portfolios', 'overview', ''];

function useRows() {
  const q = useQuery({ queryKey: OVERVIEW, queryFn: () => agreementPortfoliosApi.overview({}) });
  return { q, rows: q.data?.items ?? [] };
}
function useModels() {
  const q = useQuery({ queryKey: ['models', 'list'], queryFn: () => modelsApi.models() });
  const models = q.data?.items ?? [];
  return { models, name: (id: string | null | undefined) => (id ? models.find((m) => m.id === id)?.name ?? '?' : null),
    options: models.map((m) => ({ value: m.id, label: m.name })) };
}
async function refreshAll(qc: ReturnType<typeof useQueryClient>) {
  for (const key of [['agreement-portfolios'], ['portfolio-consolidations'], ['portfolios'], ['change-requests'], ['models']]) {
    await qc.invalidateQueries({ queryKey: key });
  }
}
function savedToast(applied: number, filed: number, nFail: number) {
  const parts = [];
  if (applied) parts.push(`${applied} change${applied === 1 ? '' : 's'} applied`);
  if (filed) parts.push(`${filed} filed for approval`);
  if (nFail) parts.push(`${nFail} not saved (see the rows in red)`);
  notifications.show({ color: nFail ? 'yellow' : filed ? 'orange' : 'green', title: nFail ? 'Partly saved' : 'Saved',
    message: parts.join(' · ') || 'Nothing to save.' });
}

export function AssignTab({ onOpenPortfolio }: { onOpenPortfolio: (ref: string) => void }) {
  const { confirmDiscardAsync } = useDirtyForm();
  const [inner, setInner] = useState<string>('aam');
  const switchTab = (v: string | null) => {
    if (!v || v === inner) return;
    void confirmDiscardAsync().then((ok) => { if (ok) setInner(v); });
  };
  return (
    <Tabs value={inner} onChange={switchTab} keepMounted={false}>
      <Tabs.List mb="sm">
        <Tabs.Tab value="aam" leftSection={<IconFileDescription size={14} />}>Attach to Agreements and Models</Tabs.Tab>
        <Tabs.Tab value="ac" leftSection={<IconStack2 size={14} />}>Attach to Consolidations</Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="aam"><AgreementsModelsTab onOpenPortfolio={onOpenPortfolio} /></Tabs.Panel>
      <Tabs.Panel value="ac"><ConsolidationsTab onOpenPortfolio={onOpenPortfolio} /></Tabs.Panel>
    </Tabs>
  );
}

function PortfolioCell({ r, onOpen, failed }: { r: PortfolioLinkRow; onOpen: (ref: string) => void; failed?: string }) {
  return (
    <>
      <Tooltip withArrow label="Open in View portfolio">
        <Anchor size="sm" fw={600} onClick={() => onOpen(r.ref)}>{r.label}</Anchor>
      </Tooltip>
      {failed && <Text size="xs" c="red">{failed}</Text>}
    </>
  );
}

function PendingLinkBadges({ r }: { r: PortfolioLinkRow }) {
  return (
    <>
      {r.pending_links.map((p) => (
        <Tooltip key={p.cr_id ?? p.agreement_label} withArrow label="Filed for approval: open the change request">
          <Badge component={Link} to={`/change-requests/${p.cr_id}`} color="blue" variant="light" style={{ cursor: 'pointer' }}>
            Pending · Link to {p.agreement_label}
          </Badge>
        </Tooltip>
      ))}
      {r.pending_unlink && (
        <Tooltip withArrow label="Filed for approval: open the change request">
          <Badge component={Link} to={`/change-requests/${r.pending_unlink.cr_id}`} color="blue" variant="light" style={{ cursor: 'pointer' }}>
            Pending · Unlink
          </Badge>
        </Tooltip>
      )}
    </>
  );
}

// ── Attach to Agreements and Models ─────────────────────────────────────────────────────────

function AgreementsModelsTab({ onOpenPortfolio }: { onOpenPortfolio: (ref: string) => void }) {
  const { can } = usePermissions();
  const canLink = can('portfolios.manage');
  const canModels = can('portfolios.models');
  const qc = useQueryClient();
  const { q, rows } = useRows();
  const m = useModels();
  const [editMode, setEditMode] = useState(false);
  const [stAgr, setStAgr] = useState<Record<string, string | null>>({});
  const [stModel, setStModel] = useState<Record<string, string | null>>({});
  const [failed, setFailed] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);

  const agrOf = (r: PortfolioLinkRow) => (r.ref in stAgr ? stAgr[r.ref] : r.link?.agreement_id ?? null);
  const modelOf = (r: PortfolioLinkRow) => (r.ref in stModel ? stModel[r.ref] : r.model?.id ?? null);
  const agrChanges = rows.filter((r) => r.ref in stAgr && (r.link?.agreement_id ?? null) !== stAgr[r.ref]);
  const modelChanges = rows.filter((r) => r.ref in stModel && (r.model?.id ?? null) !== stModel[r.ref]);
  const nChanges = agrChanges.length + modelChanges.length;
  const dirty = editMode && nChanges > 0;

  const onSave = async (): Promise<boolean> => {
    setSaving(true);
    let applied = 0, filed = 0;
    const fails: Record<string, string> = {};
    for (const r of agrChanges) {   // one portfolio at a time, unlink before link
      const target = stAgr[r.ref];
      try {
        if (r.link?.id) { const res = await agreementPortfoliosApi.unlink(r.link.id); if (res.pending) filed += 1; else applied += 1; }
        if (target && r.bank_id) {
          const res = await agreementPortfoliosApi.link({ agreement_id: target, bank_id: r.bank_id, portfolio_number: r.portfolio_number });
          if (res.pending) filed += 1; else applied += 1;
        }
      } catch (e) { fails[r.ref] = errMsg(e); }
    }
    for (const r of modelChanges) {
      if (!r.link?.id || r.ref in stAgr) continue;   // the Model cell is greyed then (link first)
      try { await portfolioModelsApi.setPortfolio(r.link.id, stModel[r.ref]); applied += 1; }
      catch (e) { fails[r.ref] = errMsg(e); }
    }
    setSaving(false);
    setFailed(fails);
    await refreshAll(qc);
    savedToast(applied, filed, Object.keys(fails).length);
    if (Object.keys(fails).length === 0) { setStAgr({}); setStModel({}); setEditMode(false); return true; }
    return false;
  };
  const confirmDiscard = useDirtyFormGuard(dirty, onSave);
  useReportTabDirty(dirty);
  const discard = () => confirmDiscard(() => { setStAgr({}); setStModel({}); setFailed({}); setEditMode(false); });

  const cols: GridCol<PortfolioLinkRow>[] = useMemo(() => [
    { key: 'portfolio', label: 'Portfolio', ft: 'text', text: (r) => r.label },
    { key: 'latest', label: 'Latest positions', ft: 'date', width: 120, text: (r) => r.latest_date },
    { key: 'agreement', label: 'Agreement', ft: 'enum', text: (r) => r.link?.agreement_label ?? 'Not linked' },
    { key: 'model', label: 'Model', ft: 'enum', width: 260, text: (r) => r.model?.name ?? 'No model' },
  ], []);
  const g = useGridHeaders(rows, cols, 'portfolios');

  const editReason = !(canLink || canModels)
    ? 'Changing these needs portfolios.manage (agreements) or portfolios.models (models).'
    : rows.length === 0 ? 'No portfolio to assign yet.' : undefined;

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="flex-end">
        <Text size="xs" c="dimmed" maw={900}>
          A portfolio belongs to one agreement at a time (also changed on the agreement&apos;s Portfolios tab; a link an RM
          files there waits for Compliance and shows here in blue). Its model is the one offered by default when the
          portfolio is viewed or traded on its own; another can always be chosen there.
        </Text>
        {!editMode ? (
          <DisabledTooltipButton leftSection={<IconPencil size={16} />} onClick={() => setEditMode(true)} disabledReason={editReason}>Edit</DisabledTooltipButton>
        ) : (
          <Group gap="xs">
            <DisabledTooltipButton variant="light" leftSection={<IconStack2 size={16} />}
              disabledReason={canModels ? undefined : 'Choosing models needs the "portfolios.models" permission.'}
              onClick={() => setApplyOpen(true)}>Apply a model to an agreement…</DisabledTooltipButton>
            <Button variant="default" leftSection={<IconArrowBackUp size={16} />} onClick={discard}>Discard</Button>
            <DisabledTooltipButton color="azul" loading={saving} disabledReason={dirty ? undefined : 'Nothing has changed.'}
              onClick={() => void onSave()}>Save changes{nChanges ? ` (${nChanges})` : ''}</DisabledTooltipButton>
          </Group>
        )}
      </Group>
      {q.error && <Alert color="red">{errMsg(q.error)}</Alert>}
      {g.summary}
      <Table striped withTableBorder fz="sm" verticalSpacing={4}>
        <Table.Thead><Table.Tr>{cols.map((c) => g.head(c))}</Table.Tr></Table.Thead>
        <Table.Tbody>
          {g.shown.length === 0 && !q.isLoading && (
            <Table.Tr><Table.Td colSpan={4}><Text size="sm" c="dimmed">No portfolio to show.</Text></Table.Td></Table.Tr>
          )}
          {g.shown.map((r) => {
            const agr = agrOf(r);
            const agrChanged = r.ref in stAgr && (r.link?.agreement_id ?? null) !== stAgr[r.ref];
            const mid = modelOf(r);
            const modelChanged = r.ref in stModel && (r.model?.id ?? null) !== stModel[r.ref];
            const pending = r.pending_links.length > 0 || !!r.pending_unlink;
            const agrLock = !canLink ? 'Changing agreements needs the "portfolios.manage" permission.'
              : pending ? 'A change to this portfolio is filed for approval: wait for it'
              : r.consolidation ? `In consolidation ${r.consolidation.name}: take it out first (tab "Attach to Consolidations")`
              : null;
            const modelLock = !canModels ? 'Choosing models needs the "portfolios.models" permission.'
              : !r.link || agrChanged ? 'A portfolio’s model is kept with its agreement link: save the link first.' : null;
            return (
              <Table.Tr key={r.ref} bg={failed[r.ref] ? 'var(--mantine-color-red-0)' : (agrChanged || modelChanged) ? 'var(--mantine-color-orange-0)' : undefined}>
                <Table.Td><PortfolioCell r={r} onOpen={onOpenPortfolio} failed={failed[r.ref]} /></Table.Td>
                <Table.Td>{r.latest_date}</Table.Td>
                <Table.Td>
                  <Stack gap={4}>
                    {editMode ? (
                      <Tooltip withArrow multiline w={320} disabled={!agrLock} label={agrLock}>
                        <div style={{ maxWidth: 360 }}>
                          <AgreementPicker label="" value={agr} disabled={!!agrLock} size="xs" onChange={(a) => setStAgr((s) => ({ ...s, [r.ref]: a }))} />
                        </div>
                      </Tooltip>
                    ) : r.link ? (
                      <Anchor component={Link} to={`/agreements/${r.link.agreement_id}`} size="sm">{r.link.agreement_label}</Anchor>
                    ) : <Badge color="gray" variant="light">Not linked</Badge>}
                    <Group gap={6}>
                      {agrChanged && <Badge color="orange" variant="light">Pending Save · {agr ? (r.link ? 'move' : 'link') : 'unlink'}</Badge>}
                      <PendingLinkBadges r={r} />
                    </Group>
                  </Stack>
                </Table.Td>
                <Table.Td>
                  {editMode ? (
                    <Tooltip withArrow multiline w={320} disabled={!modelLock} label={modelLock}>
                      <div>
                        <Select size="xs" data={m.options} value={mid} disabled={!!modelLock} clearable placeholder="No model"
                          onChange={(v) => setStModel((s) => ({ ...s, [r.ref]: v }))} comboboxProps={{ withinPortal: true }} />
                      </div>
                    </Tooltip>
                  ) : mid ? <Text size="sm">{m.name(mid) ?? r.model?.name}</Text> : <Text size="sm" c="dimmed">—</Text>}
                  {modelChanged && <Badge mt={4} color="orange" variant="light">Pending Save · model</Badge>}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
      <ApplyModelModal opened={applyOpen} onClose={() => setApplyOpen(false)} modelOptions={m.options}
        onApply={(agreementId, modelId) => {
          const hit = rows.filter((r) => r.link && agrOf(r) === agreementId && !(r.ref in stAgr && stAgr[r.ref] !== r.link.agreement_id));
          setStModel((s) => ({ ...s, ...Object.fromEntries(hit.map((r) => [r.ref, modelId])) }));
          notifications.show({ color: 'orange', title: 'Staged', message: hit.length
            ? `${m.name(modelId)} set on ${hit.length} portfolio${hit.length === 1 ? '' : 's'} of the agreement: Save changes to apply.`
            : 'That agreement has no linked portfolio.' });
          setApplyOpen(false);
        }} />
    </Stack>
  );
}

/** S1-Q5 (Pedro "do it now"): stage one default model on every portfolio of an agreement. */
function ApplyModelModal({ opened, onClose, modelOptions, onApply }: {
  opened: boolean; onClose: () => void; modelOptions: { value: string; label: string }[];
  onApply: (agreementId: string, modelId: string) => void;
}) {
  const [agreementId, setAgreementId] = useState<string | null>(null);
  const [modelId, setModelId] = useState<string | null>(null);
  const reason = !agreementId ? 'Choose an agreement.' : !modelId ? 'Choose a model.' : undefined;
  return (
    <Modal opened={opened} onClose={onClose} centered title="Apply a model to an agreement">
      <Stack>
        <Text size="sm">Sets the default model of each of the agreement&apos;s portfolios. Nothing is saved until you click Save changes.</Text>
        <AgreementPicker label="Agreement" value={agreementId} onChange={setAgreementId} size="sm" />
        <Select label="Model" data={modelOptions} value={modelId} onChange={setModelId} searchable comboboxProps={{ withinPortal: true }} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <DisabledTooltipButton disabledReason={reason} onClick={() => { onApply(agreementId!, modelId!); setAgreementId(null); setModelId(null); }}>
            Apply
          </DisabledTooltipButton>
        </Group>
      </Stack>
    </Modal>
  );
}

// ── Attach to Consolidations ────────────────────────────────────────────────────────────────

interface ConsInfo { key: string; agreementId: string; name: string; modelId: string | null; isNew: boolean }

function ConsolidationsTab({ onOpenPortfolio }: { onOpenPortfolio: (ref: string) => void }) {
  const { can } = usePermissions();
  const canCons = can('portfolios.consolidate');
  const canModels = can('portfolios.models');
  const qc = useQueryClient();
  const { q, rows } = useRows();
  const consQ = useQuery({ queryKey: ['portfolio-consolidations'], queryFn: () => portfolioConsolidationsApi.list() });
  const m = useModels();
  const [editMode, setEditMode] = useState(false);
  const [stCons, setStCons] = useState<Record<string, string | null>>({});   // ref → consolidation key
  const [newCons, setNewCons] = useState<Record<string, { agreementId: string; name: string }>>({});
  const [renames, setRenames] = useState<Record<string, string>>({});
  const [consModel, setConsModel] = useState<Record<string, string | null>>({});
  const [failed, setFailed] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [nameAsk, setNameAsk] = useState<{ mode: 'new' | 'rename'; ref?: string; key?: string; agreementId: string; value: string } | null>(null);
  const [counter, setCounter] = useState(1);

  const consolidations: Record<string, ConsInfo> = useMemo(() => {
    const out: Record<string, ConsInfo> = {};
    for (const c of consQ.data?.items ?? []) {
      out[c.id] = { key: c.id, agreementId: c.agreement_id, name: renames[c.id] ?? c.name, modelId: c.model_id, isNew: false };
    }
    for (const [k, v] of Object.entries(newCons)) out[k] = { key: k, agreementId: v.agreementId, name: v.name, modelId: null, isNew: true };
    return out;
  }, [consQ.data, newCons, renames]);

  const consOf = (r: PortfolioLinkRow) => (r.ref in stCons ? stCons[r.ref] : r.consolidation?.id ?? null);
  const modelOfCons = (key: string) => (key in consModel ? consModel[key] : consolidations[key]?.modelId ?? null);
  const members = useMemo(() => {
    const mm: Record<string, PortfolioLinkRow[]> = {};
    for (const r of rows) { const k = r.ref in stCons ? stCons[r.ref] : r.consolidation?.id ?? null; if (k) (mm[k] ??= []).push(r); }
    return mm;
  }, [rows, stCons]);
  const origMembers = useMemo(() => {
    const mm: Record<string, string[]> = {};
    for (const r of rows) if (r.consolidation) (mm[r.consolidation.id] ??= []).push(r.ref);
    return mm;
  }, [rows]);

  const consTouched = Object.keys(consolidations).filter((k) => {
    const now = (members[k] ?? []).map((r) => r.ref).sort().join(',');
    const was = (origMembers[k] ?? []).slice().sort().join(',');
    return consolidations[k].isNew || now !== was || k in renames;
  });
  const modelChanges = Object.entries(consModel).filter(([k, v]) => consolidations[k] && (consolidations[k].isNew ? v !== null : v !== consolidations[k].modelId));
  const nChanges = consTouched.filter((k) => !(consolidations[k].isNew && !(members[k] ?? []).length)).length
    + modelChanges.filter(([k]) => !consolidations[k].isNew).length;
  const dirty = editMode && nChanges > 0;

  const saveBlock = (() => {
    for (const k of consTouched) {
      const ms = members[k] ?? [];
      if (ms.length === 1) return `Consolidation '${consolidations[k].name}' would keep only ${ms[0].label}: a consolidation needs at least 2 portfolios. Add another portfolio, or delete the consolidation.`;
    }
    return null;
  })();

  const resetAll = () => { setStCons({}); setNewCons({}); setRenames({}); setConsModel({}); setFailed({}); };

  const onSave = async (): Promise<boolean> => {
    if (saveBlock) { notifications.show({ color: 'red', title: 'Not saved', message: saveBlock }); return false; }
    setSaving(true);
    let applied = 0;
    const fails: Record<string, string> = {};
    // Deletions, then those that only lose portfolios, then the others, then new ones: a portfolio
    // moved from one consolidation to another is released before it is added.
    const step = (k: string) => {
      if (consolidations[k].isNew) return 3;
      const now = (members[k] ?? []).map((r) => r.ref);
      if (now.length === 0) return 0;
      return now.some((ref) => !(origMembers[k] ?? []).includes(ref)) ? 2 : 1;
    };
    const idOf: Record<string, string> = {};
    for (const k of [...consTouched].sort((a, b) => step(a) - step(b))) {
      const c = consolidations[k];
      const ids = (members[k] ?? []).map((r) => r.link?.id).filter((x): x is string => !!x);
      try {
        if (c.isNew) {
          if (ids.length === 0) continue;
          const out = await portfolioConsolidationsApi.create({ agreement_id: c.agreementId, name: c.name, link_ids: ids });
          idOf[k] = out.id; applied += 1;
        } else if (ids.length === 0) { await portfolioConsolidationsApi.remove(k); applied += 1; }
        else { await portfolioConsolidationsApi.update(k, { name: c.name, link_ids: ids }); applied += 1; }
      } catch (e) { for (const r of members[k] ?? []) fails[r.ref] = errMsg(e); }
    }
    for (const [k, mid] of modelChanges) {
      const id = consolidations[k].isNew ? idOf[k] : k;
      if (!id || (members[k] ?? []).length === 0) continue;
      try { await portfolioModelsApi.setConsolidation(id, mid); applied += 1; }
      catch (e) { for (const r of members[k] ?? []) fails[r.ref] = errMsg(e); }
    }
    setSaving(false);
    setFailed(fails);
    await refreshAll(qc);
    savedToast(applied, 0, Object.keys(fails).length);
    if (Object.keys(fails).length === 0) { resetAll(); setEditMode(false); return true; }
    if (Object.keys(idOf).length) {   // what was created is no longer "new"
      setStCons((s) => Object.fromEntries(Object.entries(s).map(([ref, k]) => [ref, k && idOf[k] ? idOf[k] : k])));
      setNewCons((n) => Object.fromEntries(Object.entries(n).filter(([k]) => !idOf[k])));
      setConsModel((mm) => Object.fromEntries(Object.entries(mm).map(([k, v]) => [idOf[k] ?? k, v])));
    }
    return false;
  };
  const confirmDiscard = useDirtyFormGuard(dirty, onSave);
  useReportTabDirty(dirty);
  const discard = () => confirmDiscard(() => { resetAll(); setEditMode(false); });

  const setRowCons = (r: PortfolioLinkRow, v: string | null) => {
    if (v === NEW) { setNameAsk({ mode: 'new', ref: r.ref, agreementId: r.link!.agreement_id!, value: '' }); return; }
    setStCons((s) => ({ ...s, [r.ref]: v === NONE ? null : v }));
  };
  const deleteCons = (key: string) => setStCons((s) => {
    const next = { ...s };
    for (const r of members[key] ?? []) next[r.ref] = null;
    return next;
  });
  const nameTaken = (agreementId: string, name: string, exceptKey?: string) =>
    Object.values(consolidations).some((c) => c.agreementId === agreementId && c.key !== exceptKey
      && c.name.trim().toLowerCase() === name.trim().toLowerCase() && ((members[c.key] ?? []).length > 0 || c.isNew));
  const nameError = !nameAsk ? null
    : nameAsk.value.trim() === '' ? 'Type a name.'
    : nameAsk.value.trim().length > 80 ? 'At most 80 characters.'
    : nameTaken(nameAsk.agreementId, nameAsk.value, nameAsk.key) ? 'This agreement already has a consolidation with that name.' : null;
  const confirmName = () => {
    if (!nameAsk) return;
    const name = nameAsk.value.split(/\s+/).filter(Boolean).join(' ');
    if (nameAsk.mode === 'new') {
      const key = `new:${counter}`;
      setCounter((c) => c + 1);
      setNewCons((n) => ({ ...n, [key]: { agreementId: nameAsk.agreementId, name } }));
      if (nameAsk.ref) setStCons((s) => ({ ...s, [nameAsk.ref!]: key }));
    } else if (nameAsk.key) {
      const k = nameAsk.key;
      if (consolidations[k]?.isNew) setNewCons((n) => ({ ...n, [k]: { ...n[k], name } }));
      else setRenames((rn) => ({ ...rn, [k]: name }));
    }
    setNameAsk(null);
  };

  const consName = (r: PortfolioLinkRow) => { const k = consOf(r); return k ? consolidations[k]?.name ?? r.consolidation?.name ?? '?' : null; };
  const cols: GridCol<PortfolioLinkRow>[] = [
    { key: 'portfolio', label: 'Portfolio', ft: 'text', text: (r) => r.label },
    { key: 'agreement', label: 'Agreement', ft: 'enum', text: (r) => r.link?.agreement_label ?? 'Not linked' },
    { key: 'consolidation', label: 'Consolidation', ft: 'enum', width: 280, text: (r) => consName(r) ?? 'None' },
    { key: 'model', label: 'Consolidation model', ft: 'enum', width: 260,
      text: (r) => { const k = consOf(r); return k ? (m.name(modelOfCons(k)) ?? 'No model') : '—'; } },
  ];
  const g = useGridHeaders(rows, cols, 'portfolios');
  const editReason = !(canCons || canModels)
    ? 'Changing these needs portfolios.consolidate (consolidations) or portfolios.models (their models).'
    : rows.length === 0 ? 'No portfolio yet.' : undefined;

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="flex-end">
        <Text size="xs" c="dimmed" maw={900}>
          A consolidation is a set of at least two portfolios of the same agreement, traded as one. Its model is offered by
          default when the consolidation is viewed or traded; it does not change its portfolios&apos; own models (tab
          &quot;Attach to Agreements and Models&quot;).
        </Text>
        {!editMode ? (
          <DisabledTooltipButton leftSection={<IconPencil size={16} />} onClick={() => setEditMode(true)} disabledReason={editReason}>Edit</DisabledTooltipButton>
        ) : (
          <Group gap="xs">
            <Button variant="default" leftSection={<IconArrowBackUp size={16} />} onClick={discard}>Discard</Button>
            <DisabledTooltipButton color="azul" loading={saving} disabledReason={!dirty ? 'Nothing has changed.' : saveBlock ?? undefined}
              onClick={() => void onSave()}>Save changes{nChanges ? ` (${nChanges})` : ''}</DisabledTooltipButton>
          </Group>
        )}
      </Group>
      {(q.error || consQ.error) && <Alert color="red">{errMsg(q.error ?? consQ.error)}</Alert>}
      {editMode && dirty && saveBlock && <Alert color="red" py={6}>{saveBlock}</Alert>}
      {g.summary}
      <Table striped withTableBorder fz="sm" verticalSpacing={4}>
        <Table.Thead><Table.Tr>{cols.map((c) => g.head(c))}</Table.Tr></Table.Thead>
        <Table.Tbody>
          {g.shown.length === 0 && !q.isLoading && (
            <Table.Tr><Table.Td colSpan={4}><Text size="sm" c="dimmed">No portfolio to show.</Text></Table.Td></Table.Tr>
          )}
          {g.shown.map((r) => {
            const ck = consOf(r);
            const consChanged = r.ref in stCons && (r.consolidation?.id ?? null) !== stCons[r.ref];
            const mid = ck ? modelOfCons(ck) : null;
            const modelChanged = !!ck && ck in consModel && (consolidations[ck]?.isNew ? consModel[ck] !== null : consModel[ck] !== consolidations[ck]?.modelId);
            const pending = !!r.pending_unlink || r.pending_links.length > 0;
            const agr = r.link?.agreement_id ?? null;
            const consLock = !canCons ? 'Changing consolidations needs the "portfolios.consolidate" permission.'
              : !agr ? 'Link the portfolio to an agreement first (tab "Attach to Agreements and Models").'
              : pending ? 'A change to this portfolio’s link is filed for approval: wait for it'
              : null;
            const modelLock = !ck ? 'Put the portfolio in a consolidation first.'
              : !canModels ? 'Choosing models needs the "portfolios.models" permission.' : null;
            const options = [
              { value: NONE, label: 'None' },
              ...Object.values(consolidations).filter((c) => c.agreementId === agr && (c.isNew || (members[c.key] ?? []).length > 0 || c.key === ck))
                .map((c) => ({ value: c.key, label: c.name })),
              { value: NEW, label: 'New consolidation…' },
            ];
            return (
              <Table.Tr key={r.ref} bg={failed[r.ref] ? 'var(--mantine-color-red-0)' : (consChanged || modelChanged) ? 'var(--mantine-color-orange-0)' : undefined}>
                <Table.Td><PortfolioCell r={r} onOpen={onOpenPortfolio} failed={failed[r.ref]} /></Table.Td>
                <Table.Td>
                  {r.link ? <Anchor component={Link} to={`/agreements/${r.link.agreement_id}`} size="sm">{r.link.agreement_label}</Anchor>
                    : <Badge color="gray" variant="light">Not linked</Badge>}
                </Table.Td>
                <Table.Td>
                  {editMode ? (
                    <Group gap={4} wrap="nowrap">
                      <Tooltip withArrow multiline w={320} disabled={!consLock} label={consLock}>
                        <div style={{ flex: 1 }}>
                          <Select size="xs" data={options} value={ck ?? NONE} disabled={!!consLock} allowDeselect={false}
                            onChange={(v) => setRowCons(r, v)} comboboxProps={{ withinPortal: true }} />
                        </div>
                      </Tooltip>
                      {ck && !consLock && (
                        <Menu withinPortal position="bottom-end">
                          <Menu.Target>
                            <Tooltip withArrow label="Rename or delete this consolidation"><ActionIcon variant="subtle" size="sm"><IconDots size={14} /></ActionIcon></Tooltip>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item onClick={() => setNameAsk({ mode: 'rename', key: ck, agreementId: agr!, value: consolidations[ck]?.name ?? '' })}>Rename…</Menu.Item>
                            <Menu.Item color="red" onClick={() => deleteCons(ck)}>Delete (its portfolios are traded on their own again)</Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      )}
                    </Group>
                  ) : ck ? <Badge variant="outline" color="gray">{consName(r)}</Badge> : <Text size="sm" c="dimmed">—</Text>}
                  {consChanged && <Badge mt={4} color="orange" variant="light">Pending Save · {ck ? 'join' : 'leave'}</Badge>}
                </Table.Td>
                <Table.Td>
                  {editMode ? (
                    <Tooltip withArrow multiline w={320} disabled={!modelLock} label={modelLock}>
                      <div>
                        <Select size="xs" data={m.options} value={mid} disabled={!!modelLock} clearable placeholder={ck ? 'None' : '—'}
                          onChange={(v) => ck && setConsModel((mm) => ({ ...mm, [ck]: v }))} comboboxProps={{ withinPortal: true }} />
                      </div>
                    </Tooltip>
                  ) : ck ? (mid ? <Text size="sm">{m.name(mid)}</Text> : <Text size="sm" c="dimmed">None</Text>)
                    : <Text size="sm" c="dimmed">—</Text>}
                  {modelChanged && <Badge mt={4} color="orange" variant="light">Pending Save · model</Badge>}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>

      <Modal opened={!!nameAsk} onClose={() => setNameAsk(null)} centered
        title={nameAsk?.mode === 'rename' ? 'Rename consolidation' : 'New consolidation'}>
        <Stack>
          <TextInput label="Name" description="Unique within the agreement; at most 80 characters." placeholder="e.g. Growth sleeve"
            data-autofocus value={nameAsk?.value ?? ''} error={nameAsk?.value && nameError ? nameError : undefined}
            onChange={(e) => { const value = e.currentTarget.value; setNameAsk((n) => (n ? { ...n, value } : n)); }} />
          {nameAsk?.mode === 'new' && (
            <Text size="xs" c="dimmed">Then put at least one more of the agreement&apos;s portfolios in it before saving. Its model starts at None.</Text>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setNameAsk(null)}>Cancel</Button>
            <DisabledTooltipButton disabledReason={nameError ?? undefined} onClick={confirmName}>OK</DisabledTooltipButton>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
